import { useEffect, useRef, useState } from "react";
import type { PayrollHoursRow } from "../../api/types";
import { formatHours } from "../../utils/formatHours";
import { MonthNav } from "./MonthNav";

/**
 * G-135 (OD-174): a manager's Finances "Hours" card — a month switch and one
 * row per person, hours only (pay is owner-only everywhere, G-108 / OD-79).
 * The ⋯ menu holds "Deduct from salary", which still opens the page's existing
 * deduction modal. The shop is named once, in the page header; there is no date
 * range and no explanation here.
 */
export function HoursCard({
  year,
  month,
  onMonthChange,
  rows,
  loading,
  canDeduct,
  onDeduct,
}: {
  year: number;
  month: number;
  onMonthChange: (year: number, month: number) => void;
  rows: PayrollHoursRow[];
  loading: boolean;
  canDeduct: boolean;
  onDeduct: (row: PayrollHoursRow) => void;
}) {
  const sorted = [...rows].sort((a, b) => a.fullName.localeCompare(b.fullName));
  const [openMenuId, setOpenMenuId] = useState<string | null>(null);
  const listRef = useRef<HTMLUListElement>(null);

  useEffect(() => {
    if (!openMenuId) return;
    const close = (e: MouseEvent | KeyboardEvent) => {
      if (e instanceof KeyboardEvent) {
        if (e.key === "Escape") setOpenMenuId(null);
        return;
      }
      if (!listRef.current?.contains(e.target as Node)) setOpenMenuId(null);
    };
    document.addEventListener("mousedown", close);
    document.addEventListener("keydown", close);
    return () => {
      document.removeEventListener("mousedown", close);
      document.removeEventListener("keydown", close);
    };
  }, [openMenuId]);

  return (
    <div className="card hours-card">
      <div className="hours-card-head">
        <h3 className="hours-card-title">Hours</h3>
        <MonthNav year={year} month={month} onChange={onMonthChange} compact />
      </div>
      {loading ? (
        <p className="muted hours-card-empty">Loading…</p>
      ) : sorted.length === 0 ? (
        <p className="muted hours-card-empty">No staff in scope for this period.</p>
      ) : (
        <ul className="hours-list" ref={listRef}>
          {sorted.map((r) => (
            <li key={r.userId} className="hours-row">
              <span className="hours-row-name">
                {r.fullName}
                {r.isActive ? null : <span className="badge"> inactive</span>}
                {r.inProgressShiftCount > 0 ? (
                  <span
                    className="badge warning"
                    title="A shift still in progress counts once it is ended (D-32)"
                  >
                    {r.inProgressShiftCount} not ended
                  </span>
                ) : null}
              </span>
              <span className="hours-row-hours">{formatHours(r.hoursWorked)}</span>
              {/* OD-151: an owner is listed for hours only — no deduction. */}
              {canDeduct && r.role !== "OWNER" && r.isActive ? (
                <div className="hours-row-menu">
                  <button
                    type="button"
                    className="button plain hours-menu-button"
                    aria-label={`Actions for ${r.fullName}`}
                    aria-haspopup="menu"
                    aria-expanded={openMenuId === r.userId}
                    onClick={() => setOpenMenuId(openMenuId === r.userId ? null : r.userId)}
                  >
                    ⋯
                  </button>
                  {openMenuId === r.userId ? (
                    <div className="hours-menu" role="menu">
                      <button
                        type="button"
                        role="menuitem"
                        className="hours-menu-item"
                        onClick={() => {
                          setOpenMenuId(null);
                          onDeduct(r);
                        }}
                      >
                        Deduct from salary
                      </button>
                    </div>
                  ) : null}
                </div>
              ) : (
                <span className="hours-row-menu" />
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
